import { useEffect, useState } from "react";
import type { ModelWithIsCompatibleAndTags } from "../../../types.ts";
import type { Asset } from "../../types.ts";

export const usePromptWarnings = ({
  prompt,
  selectedModels,
  selectedAsset,
}: {
  prompt: string;
  selectedModels?: ModelWithIsCompatibleAndTags[];
  selectedAsset?: Asset;
}) => {
  const [warnings, setWarnings] = useState<string[]>([]);
  const [previousPrompt, setPreviousPrompt] = useState(prompt);
  const [previousSelectedAsset, setPreviousSelectedAsset] =
    useState(selectedAsset);
  const [previousSelectedModels, setPreviousSelectedModels] =
    useState(selectedModels);
  const [isInGracePeriod, setIsInGracePeriod] = useState(true);

  const modelSelectionChangeWarning =
    "Your reference selection has changed. Consider updating your prompt to fit the new reference(s) selection.";
  const assetSelectionChangeWarning =
    "Your asset selection has changed. Consider updating your prompt to fit the new asset selection.";

  // XXX: We use grace period to avoid showing warnings when the component is mounted. As prompt, models selection and asset selection are not set yet, their different initializations can result in a RACE issue.
  // For instance if the models selection is set after the prompt, a warning would be shown even if no users actions have been made.
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInGracePeriod(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Skip all warnings during grace period
    if (isInGracePeriod) {
      setPreviousSelectedModels(selectedModels);
      setPreviousSelectedAsset(selectedAsset);
      setPreviousPrompt(prompt);
      return;
    }

    const previousUuids = new Set(
      previousSelectedModels?.map((m) => m.uuid) ?? [],
    );
    const currentUuids = new Set(selectedModels?.map((m) => m.uuid) ?? []);
    const hasDifferentModels =
      previousUuids.size !== currentUuids.size ||
      ![...previousUuids].every((uuid) => currentUuids.has(uuid));

    if (hasDifferentModels && prompt && previousSelectedModels) {
      setPreviousSelectedModels(selectedModels);
      setPreviousPrompt(prompt);
      setWarnings((prev) =>
        prev.includes(modelSelectionChangeWarning)
          ? prev
          : [...prev, modelSelectionChangeWarning],
      );
    } else if (
      selectedAsset?.uuid !== previousSelectedAsset?.uuid &&
      prompt &&
      previousSelectedAsset
    ) {
      setPreviousSelectedAsset(selectedAsset);
      setPreviousPrompt(prompt);
      setWarnings((prev) =>
        prev.includes(assetSelectionChangeWarning)
          ? prev
          : [...prev, assetSelectionChangeWarning],
      );
    } else if (prompt !== previousPrompt) {
      setWarnings([]);
      setPreviousPrompt(prompt);
    }
  }, [
    prompt,
    selectedAsset,
    selectedModels,
    previousPrompt,
    previousSelectedAsset,
    previousSelectedModels,
    isInGracePeriod,
  ]);

  return warnings;
};
